// pages/chat.tsx
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useChatStore, useUserStore } from '@client/store'
import {
  Sidebar,
  ChatContainer,
  fetchChatData,
  GetUserUUID,
  GetUserData,
  ChatInfoSidebar,
} from '@client/components'
import {
  Box,
  CircularProgress,
  Typography,
  Fade,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { CharacterPayload } from '@client/components'

export function ChatPage() {
  const { chat_uuid } = useParams<{ chat_uuid: string }>()
  const navigate = useNavigate()

  const {
    setUser,
    setCharacter,
    user,
    character,
  } = useChatStore()

  const { imageURL } = useUserStore()

  const [loading, setLoading] = useState(true)
  const [userUuid, setUserUuid] = useState<string | null>(null)
  const [fetchError, setFetchError] = useState(false)
  const [userDataError, setUserDataError] = useState(false)
  const [bgImage, setBgImage] = useState<string | null>(null)

  const theme = useTheme()
  const isMedium = useMediaQuery(theme.breakpoints.down('md'))

  useEffect(() => {
    const initUser = async () => {
      try {
        const uuid = await GetUserUUID()
        if (!uuid) {
          setUserDataError(true)
          setLoading(false)
          return
        }

        setUserUuid(uuid)

        const response = await GetUserData()
        if ('error' in response) {
          setUserDataError(true)
          setLoading(false)
          return
        }

        setUser({
          user_uuid: uuid,
          username: response.username,
          user_avatar: response.user_avatar || '',
        })
      } catch {
        setUserDataError(true)
      }
    }

    initUser()
  }, [setUser])

  useEffect(() => {
    const loadChat = async () => {
      if (!chat_uuid || !userUuid) return

      setLoading(true)

      try {
        const result = await fetchChatData(chat_uuid)

        if (result.is_error) {
          setFetchError(true)
          return
        }

        setCharacter(result.Character as CharacterPayload)
        setFetchError(false)
      } catch {
        setFetchError(true)
      } finally {
        setLoading(false)
      }
    }

    loadChat()
  }, [
    chat_uuid,
    userUuid,
    setCharacter,
  ])

  useEffect(() => {
    if (imageURL) setBgImage(imageURL)
    else if (character?.profile_image) setBgImage(character.profile_image)
    else setBgImage(null)
  }, [imageURL, character?.profile_image])

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          height: '100vh',
          width: '100vw',
          overflow: 'hidden',
          position: 'relative',
          bgcolor: 'gray.900',
          backgroundImage: bgImage
            ? `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.4)), url(${bgImage})`
            : 'none',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {loading || !user ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography color="white">Loading chats, please wait...</Typography>
          </Box>
        ) : fetchError || userDataError || !chat_uuid || !character ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <img
              src="https://cqtbishpefnfvaxheyqu.supabase.co/storage/v1/object/public/character-image/CDN/MascotCrying.avif"
              alt="Mascot Crying"
              style={{ width: 96, height: 96, marginBottom: 20, cursor: 'pointer' }}
              onClick={() => navigate('/Home')}
            />
            <Typography color="white">
              Unknown Chat or Character... Try again later.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Sidebar />
            </Box>

            {!isMedium && (
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <ChatInfoSidebar
                  chat_uuid={chat_uuid}
                  currentCharacter={character}
                />
              </Box>
            )}

            <Box
              sx={{
                position: 'relative',
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ChatContainer chat_uuid={chat_uuid} />
            </Box>
          </>
        )}
      </Box>
    </Fade>
  )
}